import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const FitCities = ({ cities }) => {
  const map = useMap();

  useEffect(() => {
    const coords = cities.map((c) => [c.latitude, c.longitude]);
    const apply = () => {
      map.invalidateSize();
      if (coords.length === 0) {
        map.setView([46.99, 3.16], 9);
        return;
      }
      if (coords.length === 1) {
        map.setView(coords[0], 11);
        return;
      }
      map.fitBounds(coords, { padding: [48, 48] });
    };
    apply();
    const timer = window.setTimeout(apply, 80);
    return () => window.clearTimeout(timer);
  }, [map, cities]);

  return null;
};

const cityIcon = (city) => {
  const kind = city.hasMag && city.hasSet ? 'both' : city.hasMag ? 'mag' : 'set';
  const tags = [
    city.hasMag ? '<span class="kkp-distrib-city-pin__tag is-mag">MAG</span>' : '',
    city.hasSet ? '<span class="kkp-distrib-city-pin__tag is-set">SET</span>' : '',
  ].join('');

  return L.divIcon({
    className: `kkp-distrib-city-marker kkp-distrib-city-marker--${kind}`,
    html: `<div class="kkp-distrib-city-pin">
      <span class="kkp-distrib-city-pin__name">${escapeHtml(city.ville)}</span>
      <span class="kkp-distrib-city-pin__tags">${tags}</span>
    </div>`,
    iconSize: [1, 1],
    iconAnchor: [0, 0],
    popupAnchor: [0, -28],
  });
};

export const groupDistribCities = (points) => {
  const groups = new Map();
  points.forEach((point) => {
    const ville = String(point.ville || '').trim();
    if (ville === '') return;
    const key = `${String(point.code_postal || '').trim()}|${ville.toUpperCase()}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        ville,
        code_postal: point.code_postal || '',
        qte_mag: 0,
        qte_set: 0,
        points: 0,
        lats: [],
        lngs: [],
        zones: [],
      };
      groups.set(key, group);
    }
    group.qte_mag += Number(point.qte_mag) || 0;
    group.qte_set += Number(point.qte_set) || 0;
    group.points += 1;
    if (point.zone_label && !group.zones.includes(point.zone_label)) {
      group.zones.push(point.zone_label);
    }
    if (point.latitude != null && point.longitude != null) {
      group.lats.push(Number(point.latitude));
      group.lngs.push(Number(point.longitude));
    }
  });

  return [...groups.values()]
    .filter((group) => group.qte_mag > 0 || group.qte_set > 0)
    .filter((group) => group.lats.length > 0)
    .map((group) => ({
      key: group.key,
      ville: group.ville,
      code_postal: group.code_postal,
      qte_mag: group.qte_mag,
      qte_set: group.qte_set,
      points: group.points,
      zones: group.zones,
      hasMag: group.qte_mag > 0,
      hasSet: group.qte_set > 0,
      latitude: group.lats.reduce((sum, n) => sum + n, 0) / group.lats.length,
      longitude: group.lngs.reduce((sum, n) => sum + n, 0) / group.lngs.length,
    }))
    .sort((a, b) => a.ville.localeCompare(b.ville, 'fr'));
};

const KkpDistribCitiesMap = ({ cities = [], height = 520, fill = false }) => (
  <div
    className={`kkp-distrib-map rounded overflow-hidden border${fill ? ' kkp-distrib-map--fill' : ''}`}
    style={fill ? undefined : { height }}
  >
    <MapContainer
      center={[46.99, 3.16]}
      zoom={9}
      style={{ height: '100%', width: '100%' }}
      scrollWheelZoom
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FitCities cities={cities} />
      {cities.map((city) => (
        <Marker
          key={city.key}
          position={[city.latitude, city.longitude]}
          icon={cityIcon(city)}
        >
          <Popup>
            <strong>{city.ville}</strong>
            {city.code_postal ? <> · {city.code_postal}</> : null}
            <br />
            <small>
              {city.points} point(s)
              {city.zones.length ? ` · ${city.zones.join(', ')}` : ''}
              <br />
              MAG {city.qte_mag.toLocaleString('fr-FR')} · SET {city.qte_set.toLocaleString('fr-FR')}
            </small>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  </div>
);

export default KkpDistribCitiesMap;
